/**
 * The sheet element's bind (`SheetElement.bind`), its height-only re-render,
 * one bind pass (`bindSheet`) and the structural rebind pass, over the
 * element's `SheetElementPort`. Moved out of
 * sheet-element.ts (r5 FS 2). This module owns `SheetBindState`: it is the
 * only writer of the bind generation, the settle controller, the bind's
 * presentation hold and the render promise.
 */
import { platformOf } from "@tento-lona/host";
import { type Option } from "@tento-lona/sheets";
import { StructuralCoHide } from "@tento-lona/sheets/platform";
import { SheetViewModel } from "@tento-lona";
import { Tracing } from "@tento-core/tracing";
import { $$ } from "@tento-ui/fastdom";
import { BindGeneration, type SettleDebt, type SettleResult } from "@tento-scroll/engine/scroll-engine";
import { rowGeometryOf } from "./sheet-bind-work.ts";
import { SheetBinder } from "./sheet-index-renderer.ts";
import type { SheetPresentation } from "./sheet-presentation.ts";
import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetColumn } from "./sheet-column.ts";
import { ColumnPlaceholder } from "./column-placeholder.ts";
import { sheetColumnsOf } from "./sheet-columns-of.ts";
import type { SheetElementPort } from "./sheet-element-port.ts";
import type { SheetBindState } from "./sheet-element-state.ts";
import { dragBodyMarginTopChanged } from "./sheet-legend-hosting.ts";
import { SheetElementLayout } from "./sheet-element-layout.ts";
import type { BindSheetOptions } from "./sheet-bind-options.ts";

/** Bind `sheet` (or the empty state) to the element behind `port`. A throw
 * after it took a presentation hold fails that hold, so the sheet fails
 * open, and is rethrown; an earlier navigation's hold is left to its render. */
export function bindSheetElement(port: SheetElementPort, sheet: Option<SheetViewModel.Surface<SheetBinder>>): void {
  $$.startInstrumentation("sheet", 5);

  Tracing.log("sheet/bind", {
    sheetId: sheet?.handle.network.id,
    rowCount: sheet?.joinedTree.flattened.rows.length,
  });
  Tracing.info("nav")("sheet/bind, id=", sheet?.handle.network.id);
  sheet?.joinedTree.dbg?.();

  const previous = port.core.sheet;
  const bind = port.bindState;
  const presentation = port.core.presentation;
  const isNullState = !sheet;
  const sheetChanged = previous?.id !== sheet?.id;
  const contextChanged = previous !== sheet;
  if (contextChanged) {
    port.rows.resetForContext(previous?.handle !== sheet?.handle);
    port.defaultBinder.clearRowModes?.();
  }
  if (sheetChanged) port.rows.forgetFocus();
  const timescaleChanged = previous?.timescale !== sheet?.timescale;
  // Detect renderer change - this happens when flight timezone is toggled
  // and a new sheet is created with the same ID but different renderer/mapper.
  // Only check when both sheets exist to avoid false positives on initial load.
  const rendererChanged =
    previous != null &&
    sheet != null &&
    previous.indexRenderer !== sheet.indexRenderer;
  // A re-anchor (`goToDate`) is the SAME sheet under a new mapper anchored on
  // a different day. A same-sheet rebind keeps the user's translated
  // horizontal position — right for a view-config flip, wrong here, where
  // the new anchor IS the change. Read it off the view model's `anchorDate`,
  // the day-resolution anchor, so no caller has to say so. NOT off the
  // mapper's `startDate`: that is the anchor aligned to the mapper's period
  // (a week mapper on a Thursday anchor starts on the Monday), so it moved on
  // every timescale flip whose anchor was not a Monday, every such flip read
  // as a goToDate, and the position the user had scrolled to was thrown away
  // (F6, sdk(5)-encapsulation; pinned by `view-flip-keeps-scrolled-position`).
  const anchorChanged = rendererChanged &&
    anchorDayOf(previous) !== anchorDayOf(sheet);
  // A navigation's scrub zeroed the cell heights and dismissed the children
  // of the sheet still bound here. ANY bind of that sheet while the scrub
  // stands is a full rebind: a new generation, the only thing that pays the
  // reveal debt the scrub incurred (a kept generation leaves the zeroed cells
  // unrevealed until the stall wait fails open). Today the one caller is a
  // failed open putting the previous sheet back: the element binds only on a
  // `currentSheet` change (lona app/init.ts). Pinned both ways by
  // tests/presentation-stall/cancelled-navigation-rebind.
  const needsFullRebind = sheetChanged || rendererChanged ||
    port.presentationState.navigationTransitionScrubbed;

  // Interruptible rendering: a switch to a DIFFERENT sheet (or renderer)
  // supersedes the prior sheet's queued binds. Bump the render epoch and hand
  // it to the scroll engine BEFORE the new sheet's binds are enqueued, so the
  // drain DROPS the abandoned sheet's deferred bind tail (the freeze on rapid
  // switching) while every bind queued for THIS sheet carries the new epoch.
  // Gated on needsFullRebind so a same-sheet rebind (data settle, resize,
  // timescale) keeps its epoch and never drops in-flight binds for the sheet
  // still on screen. See interruptible-rendering.md (B / L2).
  bind.lastBindStartedAtMs = performance.now();
  // Only a change of sheet IDENTITY takes the presentation (spinner, inert
  // hidden root, the reveal gate). A same-sheet view-config switch replaces
  // the renderer but stays on the sheet the user is looking at: the old view
  // keeps painting under per-column `pending-layout` marks until the new
  // config's columns bind (operator 2026-10-09: "if we're already in the same
  // sheet, do not show any loading spinners").
  let taken: Option<SheetPresentation.Ticket> = null;
  if (sheetChanged || (!presentation.held && port.$$.root.inert)) {
    if (bind.bindPresentationTicket) presentation.complete(bind.bindPresentationTicket);
    taken = bind.bindPresentationTicket = presentation.hold();
  }
  try {
    if (needsFullRebind) nextBindGeneration(port, sheet != null);
    if (bind.bindSettleController.signal.aborted) {
      bind.bindSettleController = new AbortController();
    }
    // Joins this bind's generation to its sheet so the gen-keyed sheet/emit/*
    // and header/* logs can be attributed (sheet/bind above runs pre-bump).
    Tracing.log("sheet/bind-gen", {
      gen: bind.bindGeneration,
      needsFullRebind,
      sheetChanged,
      timescaleChanged,
      sheetId: sheet?.handle.network.id ?? null,
      rows: sheet?.joinedTree.flattened.rows.length ?? 0,
    });

    // Clean up static children and row modes when sheet or timescale changes
    if (needsFullRebind || isNullState || timescaleChanged) {
      port.defaultBinder.clearRowModes?.();
      port.layoutDebugOverlayHandle.clear();
      port.core.layout.children.fixed.dismissAll();
      port.forgetSpecGenerations();
      port.fixedChildrenLifetime.retire();
    }
    // Reset the visible-columns cache on *every* (re)bind, not just full
    // rebinds. `rebindLegend` rebuilds the legend DOM (fresh per-row
    // `$content`), and date-dependent legend content — notably the frozen
    // header's "June 2026" month label — is painted by
    // `onViewportColumnsChanged` (gated below on this key). On a same-id
    // data refresh (e.g. the null-state row picker toggling/adding a row)
    // the visible date range is unchanged, so without clearing the key the
    // hook early-returns and the freshly rebuilt header legend is left blank.
    port.forgetVisibleColumns();

    // P2 superseded-sheet bail: hand the cell accessor its active/inactive state
    // across the switch so an abandoned sheet's in-flight fetch continuations
    // bail instead of avalanching the main thread (the rapid-switch freeze).
    // Deactivate the OUTGOING accessor only on a real switch — a same-sheet
    // rebind keeps the same accessor and must stay active — then activate the
    // INCOMING one. See ongoing-sheet-performance Migration P2.
    const prevPlatform = previous != null ? platformOf(previous.handle) : null;
    const nextPlatform = sheet != null ? platformOf(sheet.handle) : null;
    if (prevPlatform && prevPlatform !== nextPlatform) prevPlatform.setActive(false);
    port.adoptSheet(sheet);

    nextPlatform?.setActive(true);

    // Hide content and legend synchronously on sheet change — NOT in
    // $$.mutate — so they're invisible before the next paint. Skip for
    // data-only refreshes (same sheet) to avoid a visible legend flash.
    if (needsFullRebind) {
      if (sheetChanged) port.$$.root.scrollPort.toggleContentVisibility(false, true);
      // Per-column co-hide (gap G4): bindSheet's mutate reveals the whole
      // viewport (toggleContentVisibility(true)) in the SAME frame it commits
      // new geometry, BEFORE `bindContent(...)` queues the content drain — so
      // the viewport un-hides a frame before its cells rebind. Marking each rendered
      // column `pending-layout` keeps it hidden until ITS bind lands, so no
      // column flashes stale content at the new geometry during that window.
      port.$$.root.scrollPort.markRenderedColumnsPendingLayout(true);
      labelPendingHeaders(port);
      if (sheetChanged) port.core.element.toggleAttribute("legend-ready", false);
    }
    $$.mutate(() => {
      port.$$.root.scrollPort.headerPeek.toggleFullScreen(isNullState, true);
      port.$$.root.scrollPort.headerPeek.toggleShadow(true);
      port.activeRowOptions.update(null);
    });

    if (isNullState) {
      // Clear ARIA counts when no sheet
      $$.mutate(() => {
        port.core.layout.presentation.setGridSize(null, null);
      });
      port.layoutDebugOverlayHandle.clear();
      if (bind.bindPresentationTicket) presentation.complete(bind.bindPresentationTicket);
      return;
    }

    // Update ARIA row/column counts for screen readers
    port.updateAriaGridCounts(sheet);

    port.layoutGate.reset();
    if (port.size) port.layoutGate.setViewportWidth(port.size.width);
    port.layoutGate.setRightOccludedPx(port.rightOccludedWidthPx);
    const renderSignal = bind.bindSettleController.signal;
    const renderGeneration = bind.bindGeneration;
    const isCurrentRender = () => !renderSignal.aborted &&
      bind.bindGeneration === renderGeneration;
    let isFirstRender = true;
    const render = async () => {
      if (!isCurrentRender()) return;
      Tracing.info("ui")("core/sheet/bind/render", sheet?.handle.network.id);
      if (!isFirstRender && port.legendState.isDragResizing) {
        renderHeightOnly(port, sheet);
        return;
      }
      // Show footer background when there are non-sheet-footer rows in the footer
      const hasContentFooterRows = sheet.joinedTree.flattened.rows.some(
        (r) => r.surface === "footer" && r.descriptor.id !== "sheet-footer",
      );
      port.$$.root.scrollPort.toggleFooterBackground(hasContentFooterRows);
      const bindSheetStartedAtMs = performance.now();
      await bindSheet(port, 
        sheet,
        isFirstRender
          ? {
              animate: false,
              rebindLegend: true,
              sheetChanged: needsFullRebind,
              sheetIdChanged: sheetChanged,
              anchorChanged,
              contextDirtied: true,
              // Preserve the user's scroll when re-binding the *same* sheet id
              // (e.g. the null-state row picker rebuilds its preview VM under a
              // deterministic ephemeral id on every toggle — a new renderer
              // object forces a full rebind, but the sheet itself is unchanged).
              // Only reset to initialScrollTop when the sheet or timescale
              // actually changes — or the window was re-anchored: `skipScroll`
              // also suppresses the horizontal index scroll, so a `goToDate`
              // that skipped it would compute the new origin and stay put.
              skipScroll: !sheetChanged && !timescaleChanged && !anchorChanged,
            }
          : {
              animate: false,
              rebindLegend: true,
              sheetChanged: false,
              sheetIdChanged: false,
              anchorChanged: false,
              contextDirtied: true,
              skipScroll: true,
            },
      );
      if (!isCurrentRender()) return;
      // bindSheet wall time (layout-gate wait + bind enqueue), cold vs warm.
      Tracing.log("sheet/bindsheet-done", {
        ms: performance.now() - bindSheetStartedAtMs,
        firstRender: isFirstRender,
        sheetId: sheet.handle.network.id,
      });
      const emitStartedAtMs = performance.now();
      port.emitHandle.onBindRow(sheet);
      port.emitHandle.reconcileStaticChildren(sheet);
      // A rebind in the same bind generation can move residency (new rows,
      // new heights) without a rendered emit.
      port.emitHandle.demandChanged();

      port.decorationsHandle.rebind();
      port.layoutDebugOverlayHandle.rebind();
      // Cost of the synchronous render tail (onBindRow + static children + decorations).
      Tracing.log("sheet/emit/sync-total", {
        ms: performance.now() - emitStartedAtMs,
        gen: bind.bindGeneration,
        sheetId: sheet.handle.network.id,
      });

      // Snapshot the first-render flag for the deferred $$.mutate: by the time
      // it runs, `isFirstRender` has already flipped to false (set at the end of
      // render()), so the re-render-only fixups below must gate on this snapshot.
      const wasFirstRender = isFirstRender;

      // Call updateStaticChildSpans after binding is complete. Use $$.mutate to
      // ensure the scroll engine's render cycle has completed and cells have
      // been bound (which registers spans). Re-run the static-child position
      // correction here too, after the scroll/layout cycle has settled, so a
      // static-child label can't keep a stale (too-high) Y computed by
      // reconcileStaticChildren before header/row geometry finished settling.
      $$.mutate(() => {
        if (!isCurrentRender()) return;
        // Re-render geometry fixups, skipped on first render (the initial
        // bindSheet establishes geometry). Heights first so the static-child
        // and fixed-child Y positions resolve against fresh row offsets. Without
        // these, a data-refresh re-render leaves fixed children (all-day spans)
        // at stale positions / undismissed (zombies) and cell + row-options
        // anchor heights don't refit to content.
        if (!wasFirstRender) port.rebindCellHeights();
        port.rebindStaticChildPositions();
        if (!wasFirstRender) {
          port.core.layout.rebindFixedChildPositions();
          port.rebindFixedChildYPositions();
        }
        const visibleColumns = port.core.layout.columns.visible;
        if (visibleColumns) {
          port.defaultBinder.updateStaticChildSpans?.(sheet, visibleColumns);
        }
      });

      isFirstRender = false;
    };

    // Initial render — ongoing invalidation handled by the effect handler
    const p = render();
    bind.lastRenderPromise = p;
    const presentationTicket = bind.bindPresentationTicket;
    void p.then(() => {
      if (presentationTicket && isCurrentRender()) presentation.complete(presentationTicket);
    }, (error) => {
      if (!isCurrentRender()) return;
      Tracing.log("sheet/reveal-error", { gen: port.presentationState.revealGeneration, err: String(error) });
      if (taken) presentation.fail(taken);
    });
    bind.lastRenderPromiseId = ++bind.renderPromiseSeq;
    // Release any reveal chain deferred on "the incoming render is not
    // scheduled yet" (see `SheetPresentationState.scrubRenderPromiseId`).
    const waiters = bind.renderPromiseWaiters;
    bind.renderPromiseWaiters = [];
    for (const release of waiters) release();
    Reflect.set(window, "__sheetRenderSettled", p);
  } catch (error) {
    // A throw between the hold above and the render's complete/fail would
    // leave the hold in place: the stall fail-open needs every hold released,
    // so the sheet would sit inert and hidden with nothing on the page to
    // undo it. Fail the hold instead, which fails open, and let the
    // caller see the error. Only a hold this call took: a same-sheet rebind
    // takes none, and an earlier navigation's pending hold still settles
    // through that navigation's render.
    Tracing.log("sheet/reveal-error", { gen: port.presentationState.revealGeneration, err: String(error) });
    if (taken) presentation.fail(taken);
    throw error;
  }
}

/** Re-apply heights only, while a legend drag-resize is in progress. */
export function renderHeightOnly(port: SheetElementPort, sheet: SheetViewModel.Surface<SheetBinder>): void {
  const layout = port.core.layout;
  layout.bindScrollHeightOnly(sheet.layout.bodyHeightPx);

  const bodyMarginTop = SheetElementLayout.bodyMarginTop(sheet);
  if (dragBodyMarginTopChanged(port.legendState, bodyMarginTop)) {
    layout.presentation.setBodyMarginTop(bodyMarginTop);
  }

  port.rebindCellHeights();
  port.legendHandle.rebindHeights(sheet);
  port.rebindStaticChildPositions();
  layout.rebindFixedChildPositions();
  port.rebindFixedChildYPositions();

  // Update span-stacked fixed children heights
  const visibleColumns = layout.columns.visible;
  if (visibleColumns) {
    port.defaultBinder.updateStaticChildSpans?.(sheet, visibleColumns);
  }

  port.decorationsHandle.rebind();
  port.layoutDebugOverlayHandle.rebind();
}

/**
 * What one bind pass (`bindSheet`) reads and writes of the sheet element: the
 * bind reasons, the bind state, the presentation machine, the bind work, the
 * scroll port's settle wait and the settle record. `SheetElementPort` is one
 * (with `S` its bound surface).
 */
export interface BindSheetPort<S> {
  readonly bindReasons: { contentChanged(): void };
  readonly bindState: SheetBindState;
  readonly core: { readonly presentation: SheetPresentation };
  bindSheetWork(sheet: S, options: BindSheetOptions): Promise<void>;
  readonly $$: {
    readonly root: {
      readonly scrollPort: { whenSettled(debt: SettleDebt, signal: Option<AbortSignal>): Promise<SettleResult> };
    };
  };
  recordSettle(via: string): void;
  readonly presentationState: { readonly revealGeneration: number };
}

/**
 * One bind pass of `sheet`. A pass for another sheet (or under a presentation
 * already held) holds the presentation until it settles; a same-sheet pass,
 * a view-config switch included, never starts one.
 */
export async function bindSheet<S>(
  port: BindSheetPort<S>,
  sheet: S,
  options: BindSheetOptions,
): Promise<void> {
  port.bindReasons.contentChanged();
  const bind = port.bindState;
  const presentation = port.core.presentation;
  const signal = bind.bindSettleController.signal;
  const generation = bind.bindGeneration;
  const ticket = presentation.held || options.sheetIdChanged ? presentation.hold() : null;
  const release = () => { if (ticket) presentation.complete(ticket); };
  signal.addEventListener("abort", release, { once: true });
  try {
    await port.bindSheetWork(sheet, options);
    if (signal.aborted || bind.bindGeneration !== generation) {
      signal.removeEventListener("abort", release);
      release();
      return;
    }
    if (ticket) {
      // Observe the drain without changing the render promise's historical
      // meaning (geometry committed and content enqueued, not fully painted).
      void port.$$.root.scrollPort.whenSettled({ kind: "this-bind", generation }, signal)
        .then((result) => {
          if (signal.aborted || result.kind === "cancelled") { release(); return; }
          port.recordSettle(result.via);
          if (result.kind === "exhausted") presentation.exhausted(ticket);
          release();
        }, (error) => {
          if (!signal.aborted) {
            Tracing.log("sheet/reveal-error", { gen: port.presentationState.revealGeneration, err: String(error) });
            presentation.fail(ticket);
          }
        }).finally(() => signal.removeEventListener("abort", release));
    } else signal.removeEventListener("abort", release);
  } catch (error) {
    signal.removeEventListener("abort", release);
    if (ticket && !signal.aborted) presentation.fail(ticket);
    else release();
    throw error;
  }
}

/**
 * One whole-sheet structural rebind pass. Only ever invoked through
 * `rebindHandle.rebindStructure`, which serializes passes and folds a
 * burst of requests into at most one trailing pass.
 */
export async function rebindStructurePass(port: SheetElementPort): Promise<void> {
  const sheet = port.core.sheet;
  if (!sheet) return;
  // Per-column co-hide (gap G4): a same-sheet structural rebind commits new
  // body-margin-top / layout / scroll-height geometry synchronously inside
  // bindSheet's mutate, then content drains over later frames. Without a
  // co-hide the rendered columns jump to the NEW geometry still showing STALE
  // content. Mark every rendered column `pending-layout` (visibility:hidden,
  // geometry preserved) up front; the `contextDirtied: true` bind below
  // re-emits EVERY rendered column through the scroll drain (emit.all(false)
  // plus reconcile's full re-add of the rendered range), and each column's
  // onBind clears its own mark as it lands. Marked per actual PASS (not per
  // queued request) so it composes with the burst coalescer in
  // `rebindStructure`. bindGeneration is intentionally NOT bumped — a
  // same-sheet structural bind is same-generation and must not drop in-flight
  // binds for the sheet still on screen.
  // Follow-up (not solved here): the coalescer may run one trailing pass right
  // after the first reveals; if that flickers in practice, a pending-depth
  // spanning the coalesced loop is the fix.
  // …but only when it is protecting against something. The mark is
  // per-COLUMN and every column spans every row, so this is all-or-nothing:
  // if an already-rendered row moves, resizes or disappears, every column is
  // about to paint stale content at the new geometry and all of them must
  // hide; if none does — an APPEND, or the coalescer's redundant trailing
  // pass — hiding buys nothing and is simply a full-width flash. Measured on
  // dev 2026-08-25 before this narrowing: one New Row blanked all 12 rendered
  // columns, ~34ms with render-next on and ~103ms with it off.
  const committed = port.core.committedRowGeometry.committedRows;
  const coHide = committed == null
    ? { kind: "unnecessary" as const }
    : StructuralCoHide.decide(committed, rowGeometryOf(sheet));
  if (coHide.kind === "required") {
    port.$$.root.scrollPort.markRenderedColumnsPendingLayout(true);
    labelPendingHeaders(port);
  }
  Tracing.log("sheet/structural-cohide", {
    sheetId: sheet.handle.network.id,
    decision: coHide.kind,
    reason: coHide.kind === "required" ? coHide.reason : null,
    rowId: coHide.kind === "required" ? coHide.rowId : null,
  });
  await bindSheet(port, sheet, {
    animate: false,
    rebindLegend: true,
    sheetChanged: false,
    sheetIdChanged: false,
    anchorChanged: false,
    contextDirtied: true,
    skipScroll: true,
  });
  // Defer — onBind fires via $$.measure() in the scroll engine's
  // queueOnBind, so span-stacked registrations aren't available yet.
  // `updateStaticChildSpans` (→ StaticChildManager.update) also prunes
  // overlays for rows removed by the structural change.
  $$.mutate(() => {
    const visibleColumns = port.core.layout.columns.visible;
    const current = port.core.sheet;
    if (visibleColumns && current) {
      port.defaultBinder.updateStaticChildSpans?.(current, visibleColumns);
    }
  });
}

/**
 * Supersede the current bind: abort its settle work, move to the next bind
 * generation and hand it to the scroll engine (which drops the superseded
 * generation's queued binds; with no sheet it only records it).
 */
function nextBindGeneration(port: Pick<SheetElementPort, "bindState" | "$$">, hasSheet: boolean): void {
  const bind = port.bindState;
  bind.bindSettleController.abort();
  bind.bindSettleController = new AbortController();
  bind.bindGeneration = BindGeneration.next(bind.bindGeneration);
  if (hasSheet) port.$$.root.scrollPort.beginBindGeneration(bind.bindGeneration);
  else port.$$.root.scrollPort.setBindGeneration(bind.bindGeneration);
}

/**
 * Call `waiter` once the next render is scheduled (a reveal chain anchored
 * before its render existed). Returns the cancel: the waiter is dropped.
 */
export function whenIncomingRender(bind: SheetBindState, waiter: () => void): () => void {
  bind.renderPromiseWaiters.push(waiter);
  return () => {
    bind.renderPromiseWaiters = bind.renderPromiseWaiters.filter((w) => w !== waiter);
  };
}

/** Abort the current bind's settle work and drop its presentation hold (recycle, disconnect). */
export function cancelBind(bind: SheetBindState): void {
  bind.bindPresentationTicket = null;
  bind.bindSettleController.abort();
}

/** The day a sheet's column window is anchored on, as a comparable number;
 *  null when there is no sheet. The view model carries this unchanged across a
 *  view-config flip and moves it only for `goToDate`. */
function anchorDayOf(sheet: Option<SheetViewModel.Surface<SheetBinder>>): Option<number> {
  return sheet == null ? null : sheet.anchorDate.daysSinceEpoch;
}

/**
 * Bind one column the scroll engine placed (`SheetLayoutPort.initialize`'s
 * `onBind`): its cells' margins, retention and the pass the binder runs.
 */
/**
 * Label every rendered header the pending-layout mark just made stale, in the
 * same task: a header is skeleton (its label is a pure function of its
 * column's range), so the mark hides its stale cells, never the header.
 */
function labelPendingHeaders(port: SheetElementPort): void {
  const sheet = port.core.sheet;
  const columns = port.core.layout.columns;
  if (sheet == null || !columns.geometryBound) return;
  for (const { $header, column } of columns.renderedElements()) {
    ColumnPlaceholder.labelHeader(sheet, column, $header);
  }
}

export function bindColumn(
  port: SheetElementPort,
  index: number,
  item: {
    $header: HTMLElement;
    $body: HTMLElement;
    // tento-ui-js's column layout passes its ambient `Option`.
    $footer: HTMLElement | null | undefined;
  },
  widthPx: number,
  resizeOnly: boolean,
  isOffscreen: boolean,
): void {
  const sheet = port.core.sheet;
  if (sheet == null) return;
  item.$header.assignStyles({
    "--z": "",
  });
  item.$body.assignStyles({
    "--z": "",
  });
  if (item.$footer) {
    item.$footer.assignStyles({ "--z": "" });
    item.$footer.toggleAttribute("use-body-margin", false);
  }
  item.$body.toggleAttribute("use-body-margin", true);
  item.$header.toggleAttribute("use-body-margin", false);

  const renderer = sheet.indexRenderer;
  const column = renderer.mapper.resolveKey(
    ColumnIndex.Key.fromIndex(index),
  );
  const binder =
    renderer.toBinder(column) ??
    port.defaultBinder;
  const rendered = port.core.layout.columns.renderedElements();
  port.columnRetention.retainOnce($$.frameNumber(), renderer.mapper, rendered.length, () => {
    port.defaultBinder.retainColumns?.(rendered.flatMap((element) => {
      const key = renderer.mapper.resolveKey(element.column).castToDefault();
      return key == null ? [] : [key];
    }));
  });
  const defaultColumn = column.castToDefault();
  if (defaultColumn != null) for (const $column of [item.$header, item.$body, item.$footer]) {
    if ($column instanceof SheetColumn) port.recordColumnScene($column, defaultColumn);
  }
  const pass: SheetBinder.ReasonedPass = {
    update: resizeOnly ? "resize-only" : "full",
    visibility: isOffscreen ? "offscreen" : "onscreen",
    rowViewport: port.rows.input(),
    // F10: a full pass after only a row-viewport membership change may
    // keep the resident cells that still show their rows.
    reason: resizeOnly ? "content" : port.bindReasons.reasonFor(
      index,
      [sheet, sheet.layout, sheet.joinedTree.flattened.rows, renderer.mapper],
      widthPx,
    ),
  };
  binder.onBind(
    sheet,
    column,
    sheetColumnsOf(item),
    widthPx,
    pass,
    port.currentTime,
  );
}
